/**
 * 그룹 상세 페이지
 * - 그룹 기본 정보 표시
 * - 그룹 통계 카드
 * - 수정/삭제 버튼 (권한별)
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Button,
  Space,
  Tag,
  Spin,
  message,
  Alert,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { groupService } from '../../services/groupService';
import { GroupStatistics } from './components/GroupStatistics';
import { GroupEditModal } from './components/GroupEditModal';
import { GroupDeleteConfirm } from './components/GroupDeleteConfirm';
import { useAuthStore } from '../../store/authStore';
import type { GroupDetail } from '../../types/group';

export const GroupDetailPage: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();

  // 그룹 정보 상태
  const [group, setGroup] = useState<GroupDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 모달 상태
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  /**
   * 그룹 정보 조회
   */
  const fetchGroup = async () => {
    if (!groupId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await groupService.getGroupById(groupId);
      setGroup(data);
    } catch (error: any) {
      if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else {
        setError('그룹 정보를 불러올 수 없습니다');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 초기 로드
   */
  useEffect(() => {
    fetchGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  /**
   * 뒤로가기
   */
  const handleBack = () => {
    navigate('/groups');
  };

  /**
   * 수정 모달 열기
   */
  const handleEdit = () => {
    setEditModalOpen(true);
  };

  /**
   * 삭제 모달 열기
   */
  const handleDelete = () => {
    setDeleteModalOpen(true);
  };

  /**
   * 수정 성공 시 처리
   */
  const handleEditSuccess = () => {
    fetchGroup(); // 새로고침
  };

  /**
   * 삭제 성공 시 처리
   */
  const handleDeleteSuccess = () => {
    message.success('그룹이 삭제되었습니다');
    navigate('/groups'); // 목록으로 이동
  };

  // 로딩 상태
  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" tip="그룹 정보를 불러오는 중..." />
      </div>
    );
  }

  // 에러 상태
  if (error || !group) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="오류"
          description={error || '그룹 정보를 찾을 수 없습니다'}
          type="error"
          showIcon
          action={
            <Button onClick={handleBack}>
              목록으로 돌아가기
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* 헤더: 뒤로가기 + 액션 버튼 */}
      <Space
        style={{
          marginBottom: 16,
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          뒤로가기
        </Button>

        <Space>
          <Button icon={<EditOutlined />} onClick={handleEdit}>
            수정
          </Button>

          {/* admin만 삭제 버튼 표시 */}
          {currentUser?.role === 'admin' && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
            >
              삭제
            </Button>
          )}
        </Space>
      </Space>

      {/* 그룹 기본 정보 */}
      <Card
        title={
          <Space>
            <span>{group.name}</span>
            <Tag color={group.is_active ? 'green' : 'red'}>
              {group.is_active ? '활성' : '비활성'}
            </Tag>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Descriptions column={2} bordered>
          <Descriptions.Item label="그룹명" span={2}>
            {group.name}
          </Descriptions.Item>
          <Descriptions.Item label="그룹 코드" span={2}>
            {group.code}
          </Descriptions.Item>
          <Descriptions.Item label="설명" span={2}>
            {group.description || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="최대 학생 수">
            {group.max_students}명
          </Descriptions.Item>
          <Descriptions.Item label="최대 강사 수">
            {group.max_instructors}명
          </Descriptions.Item>
          <Descriptions.Item label="생성일">
            {new Date(group.created_at).toLocaleString('ko-KR')}
          </Descriptions.Item>
          <Descriptions.Item label="수정일">
            {new Date(group.updated_at).toLocaleString('ko-KR')}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 그룹 통계 */}
      <GroupStatistics statistics={group.statistics} />

      {/* 수정 모달 */}
      <GroupEditModal
        open={editModalOpen}
        group={group}
        onClose={() => setEditModalOpen(false)}
        onSuccess={handleEditSuccess}
      />

      {/* 삭제 확인 모달 */}
      <GroupDeleteConfirm
        open={deleteModalOpen}
        group={group}
        onClose={() => setDeleteModalOpen(false)}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
};

export default GroupDetailPage;
